import React, { useMemo, useState, useEffect } from "react";
import { DollarOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Row, Col, DatePicker } from "antd";
import { Bar, Pie } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import moment from 'moment';
import OrderService from 'services/order';
import { stations_location, PAYMENT_METHODS, PRODUCT_CATEGORIES } from 'constants/order';
import { formatPriceNoDecimal } from 'helper/common';
import "./styles.scss";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const { RangePicker } = DatePicker;

const currencyFormat = (v) => {
  // Làm tròn và loại bỏ thập phân trước khi hiển thị
  const rounded = Math.floor(v || 0);
  return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' ₫';
};

const StatisticsOrder = () => {
  const { t: translation } = useTranslation();

  // Hàm sắp xếp khoảng thời gian
  const sortPeriods = (periods) => {
    return [...periods].sort((a, b) => {
      if (a.includes('/') && a.split('/').length === 3) {
        return new Date(a.split('/').reverse().join('-')) - new Date(b.split('/').reverse().join('-'));
      }
      return a.localeCompare(b);
    });
  };
  const [filter, setFilter] = useState({
    startDate: moment().subtract(30, 'days').format('DD/MM/YYYY'),
    endDate: moment().format('DD/MM/YYYY'),
  });
  const [stats, setStats] = useState({});
  const [revenueByPaymentMethod, setRevenueByPaymentMethod] = useState([]);
  const [productCategorySales, setProductCategorySales] = useState([]);
  const [periods, setPeriods] = useState([]);

  const fetchStats = async () => {
    const { startDate, endDate } = filter;
    try {
      // Fetch từng API riêng biệt để tránh một API fail làm fail tất cả
      const statsRes = await OrderService.getOrderStatistics({ startDate, endDate }).catch(() => ({}));
      const revenueRes = await OrderService.getRevenueByPaymentMethod({ startDate, endDate }).catch(() => []);
      const productRes = await OrderService.getRevenueByProductCategory({ startDate, endDate }).catch(() => ({}));
      
      const next = statsRes || {};

      setStats(next);
      setRevenueByPaymentMethod([...(revenueRes || [])]);
      
      // Xử lý dữ liệu từ API đã thống kê theo khoảng thời gian
      const chartData = productRes?.chart || [];
      const allProductData = chartData.flatMap(item => 
        item.categories.map(cat => ({ ...cat, date: item.date }))
      );
      setProductCategorySales([...allProductData]);

      const apiPeriods = sortPeriods(chartData.map(item => item.date));
      setPeriods(apiPeriods);
    } catch (e) {
      setStats({});
      setRevenueByPaymentMethod([]);
      setProductCategorySales([]);
      setPeriods([]);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [filter.startDate, filter.endDate]);

  const totalRevenue = useMemo(() => {
    return Number(stats?.totalRevenueCompletedOrders || stats?.totalRevenue || stats?.totalAmount || stats?.totalRevenueSuccess || 0);
  }, [stats]);

  const computeTotalOrders = (d) => {
    if (!d) return 0;
    if (typeof d.totalOrders === 'number') return Number(d.totalOrders);
    const keys = ['closedOrders','newOrders','confirmedOrders','processingOrders','canceledOrders','paidOrders','unpaidOrders'];
    const sum = keys.reduce((s,k)=>s+Number(d[k]||0),0);
    return sum || Number(d.totalOrderCount || 0) || 0;
  };

  const totalOrders = useMemo(() => computeTotalOrders(stats), [stats]);

  const orderStatusPieData = (() => {
    // Ưu tiên dùng dữ liệu trực tiếp từ API nếu có
    if (stats.orderStatusPie && stats.orderStatusPie.labels && stats.orderStatusPie.labels.length > 0) {
      return stats.orderStatusPie;
    }

    const mappedStats = {
      ...stats,
      acceptedOrders: stats.confirmedOrders || 0,
    };
    
    // Tạo dữ liệu từ các trường riêng lẻ trong stats nếu không có dữ liệu trực tiếp
    const mapping = stations_location;
    const labels = [];
    const data = [];
    const bg = [];
    
    mapping.forEach((m) => {
      const v = Number(mappedStats[m.key] || 0);
      if (v > 0) {
        labels.push(translation(m.label));
        data.push(v);
        bg.push(m.color);
      }
    });
    
    return labels.length > 0 ? {
      labels,
      datasets: [{
        data,
        backgroundColor: bg,
      }],
    } : { labels: [], datasets: [{ data: [], backgroundColor: [] }] };
  })();

  const revenueByPaymentMethodData = (() => {
    // Dùng dữ liệu API mới
    const filteredMethods = revenueByPaymentMethod.filter(item => item.totalRevenue > 0 || item.orderCount > 0);
    const labels = filteredMethods.map(item => PAYMENT_METHODS[item.paymentMethodType]?.label || item.paymentMethodName || `Type: ${item.paymentMethodType}`);
    const data = filteredMethods.map(item => Number(item.totalRevenue || 0));
    const bg = filteredMethods.map(item => PAYMENT_METHODS[item.paymentMethodType]?.color || '#10b981');
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: bg,
        },
      ],
      filteredMethods,
    };
  })();

  const productCategoryData = useMemo(() => {
    if (!productCategorySales || productCategorySales.length === 0) return { labels: [], datasets: [] };

    const categoryMap = new Map();
    const categoryRevenue = new Map();
    
    productCategorySales.forEach(item => {
      const categoryKey = item.categoryName || item.productCategoryName || item.category || item.name || 'Khác';
      
      // Cộng doanh thu từng category
      if (!categoryRevenue.has(categoryKey)) {
        categoryRevenue.set(categoryKey, 0);
      }
      categoryRevenue.set(categoryKey, categoryRevenue.get(categoryKey) + Number(item.totalRevenue || 0));
      
      // Nhóm dữ liệu theo category và date
      if (!categoryMap.has(categoryKey)) {
        categoryMap.set(categoryKey, {});
      }
      const categoryData = categoryMap.get(categoryKey);
      if (!categoryData[item.date]) {
        categoryData[item.date] = { totalRevenue: 0, totalQuantity: 0 };
      }
      categoryData[item.date].totalRevenue += Number(item.totalRevenue || 0);
      categoryData[item.date].totalQuantity += Number(item.totalQuantity || 0);
    });
    
    const validCategories = Array.from(categoryRevenue.entries())
      .filter(([key, revenue]) => revenue > 0)
      .map(([key]) => key)
      .sort();
    
    // Tạo màu động dựa trên HSL color space
    const generateColor = (index, total) => {
      const goldenRatio = 0.618033988749895;
      const hue = (index * 360 * goldenRatio) % 360;
      const saturation = 75 + (index % 3) * 5;
      const lightness = 45 + (index % 2) * 5;
      return `hsl(${Math.round(hue)}, ${saturation}%, ${lightness}%)`;
    };
    
    // Tạo datasets cho từng category
    const datasets = validCategories.map((categoryKey, index) => {
      const categoryData = categoryMap.get(categoryKey);
      return {
        label: categoryKey,
        data: periods.map(date => categoryData?.[date]?.totalRevenue || 0),
        quantities: periods.map(date => categoryData?.[date]?.totalQuantity || 0),
        backgroundColor: generateColor(index, validCategories.length),
      };
    });

    return {
      labels: periods,
      datasets: datasets,
    };
  }, [productCategorySales, periods]);

  return (
    <div>
      <Row className="statistics-filter-row" gutter={[8, 8]} align="middle">
        <Col>
          <div className="statistics-time-label">Thời gian</div>
        </Col>
        <Col>
          <RangePicker
            className="statistics-range-picker"
            defaultValue={[moment(filter.startDate, 'DD/MM/YYYY'), moment(filter.endDate, 'DD/MM/YYYY')]}
            allowClear={false}
            disabledDate={(current) => current && (current < moment().subtract(12, 'months') || current > moment())}
            onChange={(dates, dateStrings) => {
              if (dates && dates[0] && dates[1]) {
                setFilter({
                  ...filter,
                  startDate: moment(dates[0]).format('DD/MM/YYYY'),
                  endDate: moment(dates[1]).format('DD/MM/YYYY'),
                });
              }
            }}
            format="DD/MM/YYYY"
          />
        </Col>
      </Row>

      {/* Hai thẻ tổng kết */}
      <Row className="statistics-summary-row" gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <div className="summary-card-revenue">
          
            <div className="summary-card-content">
              <div className="summary-card-left">
                <div className="summary-icon">
                  <DollarOutlined />
                </div>
                <div>
                  <div className="summary-title">Tổng doanh thu</div>
                  <div className="summary-value">{currencyFormat(totalRevenue)}</div>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div className="summary-card-orders">
            
            <div className="summary-card-content">
              <div className="summary-card-left">
                <div className="summary-icon">
                  <ShoppingCartOutlined />
                </div>
                <div>
                  <div className="summary-title">Tổng đơn hàng</div>
                  <div className="summary-value">{totalOrders} đơn</div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Biểu đồ doanh thu theo ngày */}
      <Row className="statistics-chart-row" gutter={[16, 16]} style={{ marginTop: 8 }}>
        <Col span={24}>
          <div className="co-card-wrap">
            <div className="co-card-title">Doanh thu theo ngày của loại sản phẩm</div>
            <div className="bar-chart-content">
              <Bar
                height={400}
                data={productCategoryData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: {
                    mode: 'index',
                    intersect: false,
                  },
                  plugins: {
                    legend: { display: true, position: 'bottom' },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const revenue = context.parsed.y;
                          // Không hiển thị nếu doanh thu = 0
                          if (revenue === 0) return null;
                          const quantity = context.dataset.quantities[context.dataIndex];
                          return [
                            `${context.dataset.label}: ${currencyFormat(revenue)}`,
                            `Số đơn hàng: ${quantity}`,
                          ];
                        },
                      },
                    },
                  },
                  scales: {
                    x: { stacked: true },
                    y: { stacked: true, beginAtZero: true },
                  },
                }}
              />
            </div>
          </div>
        </Col>
      </Row>

      {/* Hai biểu đồ tròn */}
      <Row className="statistics-pie-row" gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <div className="co-card-wrap">
            <div className="co-card-title">Trạng thái đơn hàng</div>
            <div className="pie-chart-content">
              <div className="pie-chart-container">
                <Pie
                  data={orderStatusPieData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom' },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            const v = context.parsed;
                            const data = context.chart.data.datasets[0].data;
                            const sum = data.reduce((a, b) => a + b, 0);
                            const percent = sum > 0 ? ((v / sum) * 100).toFixed(1) : 0;
                            return `${context.label}: ${v} (${percent}%)`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div className="co-card-wrap">
            <div className="co-card-title">Doanh thu theo phương thức thanh toán</div>
            <div className="pie-chart-content">
              <div className="pie-chart-container">
                <Pie
                  data={revenueByPaymentMethodData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom' },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            const v = context.parsed;
                            const data = context.chart.data.datasets[0].data;
                            const sum = data.reduce((a, b) => a + b, 0);
                            const percent = sum > 0 ? ((v / sum) * 100).toFixed(1) : 0;
                            let label = `${context.label}: ${currencyFormat(v)} (${percent}%)`;
                            if (revenueByPaymentMethodData.filteredMethods && revenueByPaymentMethodData.filteredMethods.length > 0) {
                              const item = revenueByPaymentMethodData.filteredMethods[context.dataIndex];
                              if (item && item.orderCount) {
                                label += ` - ${item.orderCount} đơn`;
                              }
                            }
                            return label;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default StatisticsOrder;